import { Box } from "@mui/material";
import React from "react";
import useTheme from "../../../../../../../common/hooks/useTheme";
import { Theme } from "../../../../../../../providers/ThemeProvider";
import { NutritionDeclarationType } from "../../model/nutritionPlanTypes";
import DeviationLinearGraph from "./DeviationLinearGraph";

type NutrientKey = keyof NutritionDeclarationType;

type Nutrient = { key: NutrientKey; title: string; unit: string };

/** Same nutrients, titles, units and grouping as the app's intake screen. */
const GROUPS: Nutrient[][] = [
  [{ key: "cals", title: "Calories", unit: "cals" }],
  [
    { key: "carbs", title: "Carbs", unit: "gr" },
    { key: "protein", title: "Protein", unit: "gr" },
    { key: "fat", title: "Fat", unit: "gr" },
  ],
  [
    { key: "fibers_grams", title: "Fiber", unit: "gr" },
    { key: "calcium_milligrams", title: "Calcium", unit: "mg" },
    { key: "vitamin_d_micrograms", title: "Vitamin D", unit: "mcg" },
    { key: "omega3_grams", title: "Omega 3", unit: "gr" },
    { key: "iron_milligrams", title: "Iron", unit: "mg" },
    { key: "cholesterol_milligrams", title: "Cholesterol", unit: "mg" },
    { key: "sugars_grams", title: "Sugars", unit: "gr" },
    { key: "saturated_fat_grams", title: "Saturated Fat", unit: "gr" },
    { key: "iodine_micrograms", title: "Iodine", unit: "mcg" },
    { key: "sodium_milligrams", title: "Sodium", unit: "mg" },
    { key: "potassium_milligrams", title: "Potassium", unit: "mg" },
    { key: "magnesium_milligrams", title: "Magnesium", unit: "mg" },
  ],
];

type Props = {
  current: NutritionDeclarationType;
  original: NutritionDeclarationType;
  /**
   * `all` lists every nutrient (the daily view). `deviationsOnly` keeps the
   * nutrients whose current value differs from the original, grouped into
   * main and micro (the meal view), and renders nothing when none differ.
   */
  mode: "all" | "deviationsOnly";
};

const TargetComparison = ({ current, original, mode }: Props) => {
  const theme: Theme = useTheme();

  const deviationOf = (key: NutrientKey) =>
    (current[key] ?? 0) - (original[key] ?? 0);

  let groups = GROUPS.slice();
  if (mode === "deviationsOnly") {
    const changed = (nutrient: Nutrient) => deviationOf(nutrient.key) !== 0;
    const main = [...GROUPS[0], ...GROUPS[1]].filter(changed);
    const micro = GROUPS[2].filter(changed);
    groups = [main, micro].filter((group) => group.length > 0);
    if (groups.length === 0) return null;
  }

  return (
    <Box sx={styles.container}>
      <Box sx={styles.sectionTitle(theme)}>Target comparison</Box>
      {groups.map((group, index) => (
        <Box key={index} sx={styles.group(theme)}>
          {group.map((nutrient) => (
            <DeviationLinearGraph
              key={nutrient.key}
              title={nutrient.title}
              baselineValue={original[nutrient.key] ?? 0}
              deviationAmount={deviationOf(nutrient.key)}
              displayValue={current[nutrient.key] ?? 0}
              unit={nutrient.unit}
            />
          ))}
        </Box>
      ))}
    </Box>
  );
};

export default TargetComparison;

const styles = {
  container: {
    display: "flex",
    flexDirection: "column" as "column",
    gap: "12px",
  },
  sectionTitle: (theme: Theme) => ({
    fontFamily: "var(--font-Regular)",
    fontSize: "14px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
    marginBottom: "4px",
  }),
  group: (theme: Theme) => ({
    display: "flex",
    flexDirection: "column" as "column",
    border: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
    borderRadius: "8px",
    padding: "16px 16px 0",
  }),
};
